<template>
  <div>
    <browser-warning/>
    <trial-subscription-warning/>
    <no-subscription-warning/>

    <div class="navigation-top align-center">
      <div class="d-flex justify-space-between pa-0" style="height: 48px; min-width: 100%">
        <div class="d-flex mr-2" style="height: 48px; line-height: 48px;">
          <brand-logo
            style="width: 72px"
            variant="mono"
            :class="['nav-logo', {'cursor-pointer': subscriptionAccessType !== 'NO_ACCESS'}]"
            @click="subscriptionAccessType !== 'NO_ACCESS' ? $router.push('/dashboard/').catch(() => {}): null"
          />
          <navigation-links v-if="subscriptionAccessType !== 'NO_ACCESS'"/>
          <workflow-selector v-if="$route.params.id && subscriptionAccessType !== 'NO_ACCESS'" ref="workflowSelector"/>
        </div>

        <slot>
        <!-- slot for injecting additional buttons -->
        </slot>

        <div class="d-flex flex-nowrap ml-8">
          <v-menu
            location="bottom center"
            :offset="[8, 0]"
            v-if="subscriptionAccessType === 'FULL'"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                class="mr-4"
                icon="mds:add_circle"
              />
            </template>

            <v-list density="compact">
              <v-list-item
                v-if="canCreateProject"
                prepend-icon="mds:grid-view"
                @click="openProjectDialog"
              >
                <v-list-item-title>New project</v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="isNewWorkflowEnabled()"
                prepend-icon="mds:account-tree"
                @click="openWorkflowDialog"
              >
                <v-list-item-title>New workflow</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <help-dialog-workflow v-if="isWorkflowPage"/>
          <help-dialog-project v-if="isProjectPage"/>
          <help-dialog-start v-if="isStartPage"/>

          <v-tooltip v-if="isWorkflowPage" location="bottom" open-delay="500" text="Start with MCP">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="mr-4 mcp-pill"
                variant="text"
                icon
                @click="showMcpDialog = true"
              >
                MCP
              </v-btn>
            </template>
          </v-tooltip>

          <v-btn
            class="mr-4"
            variant="text"
            :icon="themeIcon"
            @click="toggleTheme"
          />

          <user-profile-header/>
        </div>
      </div>
    </div>

    <start-with-mcp-dialog v-model="showMcpDialog" />

    <!--
      Hosted outside the "+" menu on purpose. A dialog placed inside a v-menu's content is destroyed
      as soon as the menu closes, and a v-select inside such a dialog treats the menu as its parent
      menu and closes it on click-outside, taking the dialog with it.
    -->
    <settings-project-dialog
      v-if="canCreateProject"
      ref="projectDialog"
      :setProject="true"
    >
      <template #activator><span></span></template>
    </settings-project-dialog>
    <workflow-dialog
      v-if="isNewWorkflowEnabled()"
      ref="workflowDialog"
      :setWorkflow="true"
      :memberships="filteredProjectMemberships"
    >
      <template #activator><span></span></template>
    </workflow-dialog>
  </div>
</template>

<script>
import UserProfileHeader from '@/components/UserProfileHeader'
import NavigationLinks from './NavigationLinks.vue'
import WorkflowSelector from './WorkflowSelector.vue'
import WorkflowDialog from './WorkflowDialog.vue'
import SettingsProjectDialog from './SettingsProjectDialog.vue'
import HelpDialogWorkflow from './HelpDialogWorkflow'
import HelpDialogProject from './HelpDialogProject'
import HelpDialogStart from './HelpDialogStart'
import { mapState } from 'vuex'
import { computed, ref } from 'vue'
import StartWithMcpDialog from '@/components/StartWithMcpDialog.vue'
import { getEntitledPlan } from '@/utils/payments'
import BrowserWarning from '@/components/BrowserWarning.vue'
import NoSubscriptionWarning from '@/components/NoSubscriptionWarning.vue'
import TrialSubscriptionWarning from '@/components/TrialSubscriptionWarning.vue'
import useAppTheme from '@/composables/useAppTheme'
import BrandLogo from '@/components/BrandLogo.vue'

export default {
  setup () {
    const { themePreference, toggleTheme } = useAppTheme()
    const themeIcon = computed(() => {
      return themePreference.value === 'dark' ? 'mds:light_mode' : 'mds:dark_mode'
    })
    const showMcpDialog = ref(false)
    return { themePreference, toggleTheme, themeIcon, showMcpDialog }
  },
  components: {
    BrandLogo,
    TrialSubscriptionWarning,
    NoSubscriptionWarning,
    BrowserWarning,
    NavigationLinks,
    WorkflowSelector,
    UserProfileHeader,
    WorkflowDialog,
    SettingsProjectDialog,
    HelpDialogWorkflow,
    HelpDialogProject,
    HelpDialogStart,
    StartWithMcpDialog
  },
  computed: {
    ...mapState({
      activeSubscription: state => state.payments.activeSubscription,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      projectMemberships: state => state.projectMemberships.projectMemberships,
      isEnterpriseUser: state => state.payments.isEnterpriseUser
    }),
    hasProjects () {
      return this.projectMemberships?.length > 0
    },
    filteredProjectMemberships () {
      return this.projectMemberships?.filter(pm => pm.project)
    },
    isWorkflowPage () {
      return !!this.$route.params.id
    },
    isProjectPage () {
      return (!!this.$route.params.projectId) && (!this.$route.params.id)
    },
    isStartPage () {
      return (!this.$route.params.projectId) && (!this.$route.params.id)
    },
    selectedPlan: function () {
      return getEntitledPlan(this.activeSubscription)
    },
    canCreateProject () {
      return !!this.selectedPlan && (this.projectMemberships?.length ?? 0) < this.selectedPlan.maxProjectsCount
    }
  },
  methods: {
    openProjectDialog () {
      this.$refs.projectDialog.openProjectDialog()
    },
    openWorkflowDialog () {
      this.$refs.workflowDialog.openWorkflowDialog()
    },
    isNewWorkflowEnabled () {
      return this.hasProjects && !this.$store.state.workflow.isViewOnlyEnabled
    }
  }
}
</script>

<style scoped>
.navigation-top {
  margin: 14px -16px;
}

.mcp-pill :deep(.v-btn__content) {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
</style>
